import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Typography,
  Stepper,
  Box,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { ExpandMoreIcon } from 'components/common/Icon';
import React, { useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Company } from 'models/company';
import {
  FormationsPlan,
  IAccount,
  ProgressTrackerGroup,
  ProgressTrackerStages,
  ProgressTrackerStatus,
} from 'models/account';
import { useCurrentAccount, useCurrentCompany } from 'hooks/api';
import { FormationsStep } from 'components/FormationsStep';
import { ENTITY_MAPPING } from 'constants/common';
import { BUSINESS_OWNERSHIP } from 'enums';
import { PersonalDetailsContainer as PersonalDetails } from './PersonalDetails';
import { CompanyDetailsContainer as CompanyDetails } from './CompanyDetails';
import { AddressInformationContainer as AddressInformation } from './AddressInformation';
import { OwnershipDetailsContainer as OwnershipDetails } from './OwnershipDetails';
import { FilingDocuments } from './FilingDocuments';
import { initialState, reducer } from './state';
import { StageSkeleton } from '../StageSkeleton';
import { AccordionCTA } from '../AccordionCTA';
import { getFirstNoCompleted } from '../util';
import { StepContentWrapper } from '../Components/StepContentWrapper';
import { StageStatusColumn, StageStatusText } from '../Components/StageStatusColumn';

const getStepsCount = (
  progress: ProgressTrackerGroup[],
  typeOfAccount: FormationsPlan,
  isMultiMemberOwnedBusiness: boolean,
  isSoleProp: boolean,
): {
  stepsCount: number;
  completed: number;
  isStageCompleted: boolean;
} => {
  const steps = [
    ProgressTrackerStages.PersonalDetails,
    ProgressTrackerStages.CompanyDetails,
    ProgressTrackerStages.AddressDetails,
  ];
  if (isMultiMemberOwnedBusiness) {
    steps.push(ProgressTrackerStages.OwnershipDetails);
  }
  if (isSoleProp) {
    steps.push(ProgressTrackerStages.FilingDocuments);
  }

  const completed = steps.filter((step) =>
    progress.some(
      (p) =>
        p.stage === step &&
        p.status.some((s) => s.status === ProgressTrackerStatus.Completed),
    ),
  );

  if (isSoleProp && completed.length === steps.length - 1) {
    // all other steps are completed, so we consider the filing documents step as completed
    // as there is no more action needed from the user
    completed.push(ProgressTrackerStages.FilingDocuments);
  }

  return {
    stepsCount: steps.length,
    completed: completed.length,
    isStageCompleted: completed.length === steps.length,
  };
};

interface TParams {
  company: Company;
  account: IAccount;
  isDesktop: boolean;
  expanded: boolean | string;
  handleExpand: (
    panel: string,
  ) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => void;
  onContinueToAccountVerification: () => void;
}

const IncorporationLayout = ({
  company,
  account: accData,
  isDesktop,
  expanded,
  handleExpand,
  onContinueToAccountVerification,
}: TParams) => {
  const { t } = useTranslation();
  const isMultiMemberOwnedBusiness =
    accData?.businessOwnerShip === BUSINESS_OWNERSHIP.PARTNERSHIP;
  // const addressState = company.contactDetails?.mailingAddress?.state
  // const taxFilingStatus = accData.generalTaxInfo?.taxFilingStatus
  // const isAdditionalOwnerNeeded = useMemo(() => needAdditionalOwner(addressState, taxFilingStatus), [addressState, taxFilingStatus]);

  const [state, dispatch] = useReducer(
    reducer,
    initialState(
      company?.entityType ?? company?.legacyEntityType ?? '',
      isMultiMemberOwnedBusiness,
    ),
  );

  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    const firstNotCompleted = getFirstNoCompleted(state, true);
    if (firstNotCompleted !== -1) {
      setActiveStep(firstNotCompleted);
    }
  }, [state]);

  useEffect(() => {
    if (accData?.progress.length > 0) {
      dispatch({
        type: ProgressTrackerStages.PersonalDetails,
        value: accData?.progress,
      });
      dispatch({
        type: ProgressTrackerStages.CompanyDetails,
        value: accData?.progress,
      });
      dispatch({
        type: ProgressTrackerStages.AddressDetails,
        value: accData?.progress,
      });
      if (isMultiMemberOwnedBusiness) {
        dispatch({
          type: ProgressTrackerStages.OwnershipDetails,
          value: accData?.progress,
        });
      }
      dispatch({
        type: ProgressTrackerStages.FilingDocuments,
        value: accData?.progress,
      });
    }
  }, [accData, isMultiMemberOwnedBusiness]);

  const steps = Object.keys(state);
  const isSoleProp = company?.entityType === ENTITY_MAPPING.sole_prop;

  const {
    stepsCount,
    completed: completedCount,
    isStageCompleted,
  } = getStepsCount(
    accData?.progress ?? [],
    FormationsPlan.FullSuite,
    isMultiMemberOwnedBusiness,
    isSoleProp,
  );

  const onBackClick = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  return (
    <Accordion
      expanded={expanded === 'incorporation'}
      onChange={handleExpand('incorporation')}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="incorporation-content"
        id="incorporation-header"
        sx={{
          alignItems: 'flex-start',
        }}
      >
        <Grid container>
          <Grid item xs={10}>
            <Typography variant="h6B">Incorporate Your Business</Typography>
            <Typography variant="subtitle1" color="primary">
              {completedCount < stepsCount && (
                <>
                  {isSoleProp
                    ? t('progressTracker.incorporation.soleProp.subTitle')
                    : t('progressTracker.incorporation.subTitle')}
                </>
              )}
              {completedCount === stepsCount &&
                isSoleProp &&
                t('progressTracker.incorporation.einPendingInfo')}
              {isStageCompleted && !isSoleProp && (
                <Box>
                  {t(
                    'progressTracker.incorporation.incorporationCompletedInfo',
                  )}
                  &nbsp;
                </Box>
              )}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {t('progressTracker.incorporation.processingTime')}
            </Typography>
            <AccordionCTA
              expanded={expanded}
              handleExpand={handleExpand}
              completed={completedCount}
              totalStep={stepsCount}
              testId="start-incorporation"
              accordionId="incorporation"
            />
          </Grid>
          <StageStatusColumn item xs={2}>
            <StageStatusText variant="h8B">
              {isStageCompleted && <CheckCircle color="secondary" />}
              {completedCount}/{stepsCount}
            </StageStatusText>
          </StageStatusColumn>
        </Grid>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={{ md: 4 }} flexDirection="row">
          <Grid item xs={12} md={3}>
            <Stepper
              activeStep={activeStep}
              nonLinear
              orientation={isDesktop ? 'vertical' : 'horizontal'}
              alternativeLabel={!isDesktop}
              data-testid="incorporation-stages"
            >
              {steps.map((key, index) => (
                <FormationsStep
                  key={key}
                  stageKey={key}
                  index={index}
                  setActiveStep={setActiveStep}
                  status={state[key]?.status}
                  label={state[key]?.label}
                  isTrainer={false}
                  activeStep={activeStep}
                  completedSteps={completedCount}
                  totalSteps={stepsCount}
                  typeOfAccount={FormationsPlan.FullSuite}
                />
              ))}
            </Stepper>
          </Grid>
          <StepContentWrapper item style={{ flex: 1 }}>
            {activeStep === 0 && <PersonalDetails company={company} />}
            {activeStep === 1 && (
              <CompanyDetails
                company={company}
                setActiveStep={setActiveStep}
                onBackClick={onBackClick}
              />
            )}
            {activeStep === 2 && accData != null && (
              <AddressInformation
                account={accData}
                company={company}
                onBackClick={onBackClick}
                hasOwnershipSection={isMultiMemberOwnedBusiness}
                onContinueToAccountVerification={
                  onContinueToAccountVerification
                }
              />
            )}
            {activeStep === 3 && accData?.id && isMultiMemberOwnedBusiness && (
              <OwnershipDetails
                account={accData}
                company={company}
                onBackClick={onBackClick}
                onContinueToAccountVerification={
                  onContinueToAccountVerification
                }
              />
            )}
            {activeStep === 3 && accData?.id && !isMultiMemberOwnedBusiness && (
              <FilingDocuments
                accountId={accData?.id}
                companyData={company}
                completedSteps={completedCount}
                stepLengthNotIncludingFilingDocumentStep={steps.length - 1}
                stageCompleted={
                  state[ProgressTrackerStages.FilingDocuments]?.status ===
                  ProgressTrackerStatus.Completed
                }
                onContinueToAccountVerification={
                  onContinueToAccountVerification
                }
                onBackClick={onBackClick}
              />
            )}
            {activeStep === 4 && accData?.id && isMultiMemberOwnedBusiness && (
              <FilingDocuments
                accountId={accData?.id}
                companyData={company}
                completedSteps={completedCount}
                stepLengthNotIncludingFilingDocumentStep={steps.length - 1}
                stageCompleted={
                  state[ProgressTrackerStages.FilingDocuments]?.status ===
                  ProgressTrackerStatus.Completed
                }
                onContinueToAccountVerification={
                  onContinueToAccountVerification
                }
                onBackClick={onBackClick}
              />
            )}
          </StepContentWrapper>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export const Incorporation = (props: {
  isDesktop: boolean;
  expanded: boolean | string;
  handleExpand: (
    panel: string,
  ) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => void;
  onContinueToAccountVerification: () => void;
}) => {
  const {
    currentCompany: companyData,
    isLoading: companyDataLoading,
    status: companyDataStatus,
  } = useCurrentCompany();
  const {
    currentAccount: accountData,
    isLoading: accountDataLoading,
    status: accountDataStatus,
  } = useCurrentAccount();

  return !companyDataLoading &&
    companyDataStatus === 'success' &&
    companyData &&
    !accountDataLoading &&
    accountDataStatus === 'success' &&
    accountData ? (
    <IncorporationLayout
      {...props}
      company={companyData}
      account={accountData}
    />
  ) : (
    <StageSkeleton />
  );
};
